import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { outreach, outreachQueries, PaginatedApiResponseData } from '@round/api';

const keys = outreachQueries.outreachRequestResults.keys;

type ResultPages = InfiniteData<PaginatedApiResponseData<outreach.OutreachResult>>;

/**
 * Writes an action's response rows straight into the mounted list's cache instead of refetching,
 * since a refetch would filter the rows the action just changed out from under whoever ran it.
 */
export function usePatchOutreachResultsCache() {
    const queryClient = useQueryClient();

    return (results: outreach.OutreachResult[]) => {
        if (!results.length) {
            return;
        }

        const resultById = new Map(results.map((result) => [result.id, result]));

        queryClient.setQueriesData<ResultPages>(
            { queryKey: keys.infiniteLists(), type: 'active' },
            (data) =>
                data && {
                    ...data,
                    pages: data.pages.map((page) => ({
                        ...page,
                        results: page.results.map((row) => resultById.get(row.id) ?? row),
                    })),
                }
        );

        // The write above resets the list's stale time, so it's marked stale again without refetching —
        // the mounted list keeps its patched rows, while the next page opened reads fresh from the server.
        queryClient.invalidateQueries({ queryKey: keys.infiniteLists(), refetchType: 'none' });
    };
}
